import { defineStore } from 'pinia';
import { fetchEffectivePermissions } from '@/composables/usePermissionsApi';
import type { PermissionGrant, PermissionOperation } from '@/types/permissions';
import * as Sentry from '@sentry/vue';
import { isNoLinkedAccountError } from '@/utils/accountLinkError';

// DEVAMS-2014: Pinia store for effective permissions resolved by the backend.
// Populated once on login from GET /api/v1/me/permissions; kept fresh via
// Mercure SSE (usePermissionsMercure) and a focus-based staleness check (App.vue).

export const usePermissionsStore = defineStore('permissions', {
    persist: {
        enabled: true,
        paths: ['grants', 'lastFetchedAt'],
    },
    state: (): {
        grants: PermissionGrant[];
        loaded: boolean;
        loading: boolean;
        lastFetchedAt: number;
        lastError: string | null;
        mercureToken: string | null;
        mercureTopic: string | null;
        usingFallback: boolean;
    } => ({
        grants: [],
        loaded: false,
        loading: false,
        lastFetchedAt: 0,
        lastError: null,
        mercureToken: null,
        mercureTopic: null,
        usingFallback: false,
    }),
    getters: {
        /**
         * Returns true when the given permission key has been granted for the
         * specified operation.
         *
         * Rules:
         *   - Returns false if the store is not yet loaded (prevents flash of
         *     false-positive access before the API call resolves).
         *   - A grant with `operations: []` is a boolean grant — access is
         *     considered granted for any operation.
         *   - A non-empty `operations` array is checked for membership.
         */
        isGranted: (state) => (permission: string, operation: PermissionOperation = 'read'): boolean => {
            if (!state.loaded && state.grants.length === 0) return false;
            const grant = state.grants.find(g => g.permission === permission);
            if (!grant) return false;
            if (grant.operations.length === 0) return true;
            return grant.operations.includes(operation);
        },
        /**
         * Alias for isGranted(permission, 'read').
         */
        hasAccess: (state) => (permission: string): boolean => {
            if (!state.loaded && state.grants.length === 0) return false;
            const grant = state.grants.find(g => g.permission === permission);
            if (!grant) return false;
            if (grant.operations.length === 0) return true;
            return grant.operations.includes('read');
        },
        /**
         * Returns true when the store data is older than 5 minutes,
         * indicating that a background refresh is advisable (e.g. on tab focus).
         */
        isStale: (state): boolean => {
            const threshold = state.usingFallback ? 30 * 1000 : 5 * 60 * 1000;
            return Date.now() - state.lastFetchedAt > threshold;
        },
    },
    actions: {
        async fetchPermissions(): Promise<void> {
            this.loading = true;
            try {
                const { data, mercureToken, mercureTopic } = await fetchEffectivePermissions();
                this.grants = data.grants;
                this.loaded = true;
                this.usingFallback = false;
                this.lastFetchedAt = Date.now();
                this.lastError = null;
                // Capture Mercure headers so usePermissionsMercure can subscribe
                if (mercureToken) this.mercureToken = mercureToken;
                if (mercureTopic) this.mercureTopic = mercureTopic;
            } catch (error: unknown) {
                this.lastError = 'Failed to load permissions';
                if (!this.loaded && this.grants.length === 0) {
                    // True first-load failure with no cached grants: activate fallback
                    // mode so the router guard does not block the user at
                    // /permissions-unavailable. All permission checks will return false
                    // (deny-by-default) until a subsequent successful fetch re-populates.
                    this.grants = [];
                    this.loaded = true;
                    this.usingFallback = true;
                    this.lastFetchedAt = Date.now();
                    if (isNoLinkedAccountError(error)) {
                        // Expected for Azure identities without a linked PCP account (DEVAMS-2760):
                        // deny-all + AccountNotLinkedOverlay is the intended outcome, not a Sentry warning.
                        Sentry.addBreadcrumb({
                            category: 'permissions',
                            level: 'warning',
                            message: 'first-load skipped: no linked PCP account — deny-all fallback',
                            data: { status: 403, errorCode: 'no_linked_account' },
                        });
                        console.warn('[permissionsStore] no linked PCP account — activating deny-all fallback');
                    } else {
                        Sentry.captureMessage('[permissionsStore] first-load failure — activating deny-all fallback', 'warning');
                    }
                } else {
                    // Transient error on a subsequent fetch: keep serving the last
                    // known set — do NOT reset loaded or grants.
                    Sentry.captureException(error, {
                        tags: { feature: 'permissions', endpoint: 'me/permissions' },
                    });
                }
                console.error('[permissionsStore] fetchPermissions failed:', error);
            } finally {
                this.loading = false;
            }
        },
    },
});
